<template>
  <teleport to="body">
    <div class="container-fluid bb-popup-outer">
      <div class="bb-popup-bg d-flex justify-content-center align-items-center">
        <div
          class="card shadow bb-card bb-popup-card mb-2 rounded bb-popup-table-card"
        >
          <div
            class="container-fluid p-4 bb-tooltip-wrapper bb-popup-table-body"
          >
            <div class="bb-rolls-header mb-1">
              <div class="bb-page-title">Party Rolls</div>
              <div class="bb-page-arena-name">{{ arenaName }}</div>
            </div>

            <div class="bb-popup-table-controls mb-3">
              <div class="row gx-3 gy-0 align-items-end">
                <div class="col">
                  <div
                    class="bb-roll-summary-row row justify-content-start gx-3 gy-0"
                  >
                    <div
                      class="col-sm-4 col-lg-2 form-group bb-overall-summary-field"
                    >
                      <div class="form-floating">
                        <input
                          readonly
                          disabled
                          class="form-control text-center"
                          :value="totalTrackedRolls"
                          id="party-rolls-total-rolls"
                        />
                        <label
                          class="bb-float-label optional"
                          for="party-rolls-total-rolls"
                          >Overall Rolls</label
                        >
                      </div>
                    </div>
                    <div
                      class="col-sm-4 col-lg-2 form-group bb-overall-summary-field"
                    >
                      <div class="form-floating">
                        <input
                          readonly
                          disabled
                          class="form-control text-center"
                          :value="totalCriticals"
                          id="party-rolls-criticals-total"
                        />
                        <label
                          class="bb-float-label optional"
                          for="party-rolls-criticals-total"
                          >Overall Criticals</label
                        >
                      </div>
                    </div>
                    <div
                      class="col-sm-4 col-lg-2 form-group bb-overall-summary-field"
                    >
                      <div class="form-floating">
                        <input
                          readonly
                          disabled
                          class="form-control text-center"
                          :value="
                            formatPercentage(totalCriticals, totalTrackedRolls)
                          "
                          id="party-rolls-criticals-pct"
                        />
                        <label
                          class="bb-float-label optional"
                          for="party-rolls-criticals-pct"
                          >Overall Critical %</label
                        >
                      </div>
                    </div>
                    <div
                      class="col-sm-4 col-lg-2 form-group bb-overall-summary-field"
                    >
                      <div class="form-floating">
                        <input
                          readonly
                          disabled
                          class="form-control text-center"
                          :value="totalFumbles"
                          id="party-rolls-fumbles-total"
                        />
                        <label
                          class="bb-float-label optional"
                          for="party-rolls-fumbles-total"
                          >Overall Fumbles</label
                        >
                      </div>
                    </div>
                    <div
                      class="col-sm-4 col-lg-2 form-group bb-overall-summary-field"
                    >
                      <div class="form-floating">
                        <input
                          readonly
                          disabled
                          class="form-control text-center"
                          :value="
                            formatPercentage(totalFumbles, totalTrackedRolls)
                          "
                          id="party-rolls-fumbles-pct"
                        />
                        <label
                          class="bb-float-label optional"
                          for="party-rolls-fumbles-pct"
                          >Overall Fumble %</label
                        >
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-auto ms-auto bb-popup-table-actions">
                  <div
                    class="btn-group btn-group-lg flex-nowrap"
                    role="group"
                  >
                    <button
                      type="button"
                      class="btn btn-lg btn-primary"
                      :disabled="isSavingUpdates || !hasPendingChanges()"
                      @click="handleClose"
                    >
                      OK
                    </button>
                    <button
                      type="button"
                      class="btn btn-lg btn-warning"
                      :disabled="isSavingUpdates"
                      @click="handleCancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="errorMessages.length > 0">
              <div class="row mt-2 mb-3">
                <div class="form-group">
                  <div class="row">
                    <div class="col text-end me-3">
                      <div
                        v-for="errorMessage in errorMessages"
                        :key="errorMessage"
                        class="bb-error-wrap"
                        style="white-space: pre-line"
                      >
                        <div class="row mb-3">
                          {{ errorMessage }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="isLoading"
              class="bb-loader bb-popup-table-loader"
            ></div>

            <div
              v-else-if="statsRows.length === 0"
              class="text-center text-muted py-4"
            >
              {{ emptyMessage }}
            </div>

            <div v-else>
              <div class="table-responsive">
                <table class="table table-borderless align-middle mb-0">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        class="bb-user-col align-middle"
                      >
                        User
                      </th>
                      <th
                        scope="col"
                        class="bb-role-col align-middle"
                      >
                        Role
                      </th>
                      <th
                        scope="col"
                        class="text-center bb-overall-position-col"
                      >
                        Overall Position
                      </th>
                      <th
                        scope="col"
                        class="text-center bb-net-rolls-col"
                      >
                        Net Rolls
                      </th>
                      <th
                        scope="col"
                        class="text-center bb-roll-pair-col bb-critical-pair-col"
                      >
                        Critical Rolls
                      </th>
                      <th
                        scope="col"
                        class="text-center bb-roll-pair-col bb-fumble-pair-col"
                      >
                        Fumble Rolls
                      </th>
                      <th
                        scope="col"
                        class="text-center bb-metric-col bb-total-rolls-col"
                      >
                        Total
                      </th>
                      <th
                        scope="col"
                        class="text-center bb-metric-col"
                      >
                        Artefact Rolls
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="statRow in displayedStatsRows"
                      :key="statRow.id"
                    >
                      <td class="bb-user-col">
                        <div
                          class="form-floating bb-total-field bb-row-metric-field bb-user-field"
                        >
                          <input
                            readonly
                            disabled
                            class="form-control text-center"
                            :value="statRow.first_name || '-'"
                            :id="`party-rolls-user-${statRow.id}`"
                          />
                          <label
                            class="bb-float-label optional"
                            :for="`party-rolls-user-${statRow.id}`"
                            >User</label
                          >
                        </div>
                      </td>
                      <td class="bb-role-col">
                        <div
                          class="form-floating bb-total-field bb-row-metric-field bb-role-field"
                        >
                          <input
                            readonly
                            disabled
                            class="form-control text-center"
                            :value="statRow.role || '-'"
                            :id="`party-rolls-role-${statRow.id}`"
                          />
                          <label
                            class="bb-float-label optional"
                            :for="`party-rolls-role-${statRow.id}`"
                            >Role</label
                          >
                        </div>
                      </td>
                      <td class="bb-overall-position-col">
                        <div
                          class="form-floating bb-total-field bb-row-metric-field bb-overall-position-field"
                        >
                          <input
                            readonly
                            disabled
                            class="form-control text-center"
                            :value="overallPositionsById[statRow.id]"
                            :id="`party-rolls-overall-position-${statRow.id}`"
                          />
                          <label
                            class="bb-float-label optional"
                            :for="`party-rolls-overall-position-${statRow.id}`"
                            >Overall Position</label
                          >
                        </div>
                      </td>
                      <td class="bb-net-rolls-col">
                        <div
                          class="form-floating bb-total-field bb-row-metric-field bb-net-rolls-field"
                        >
                          <input
                            readonly
                            disabled
                            class="form-control text-center"
                            :value="rowNetRolls(statRow)"
                            :id="`party-rolls-net-rolls-${statRow.id}`"
                          />
                          <label
                            class="bb-float-label optional"
                            :for="`party-rolls-net-rolls-${statRow.id}`"
                            >Net Rolls</label
                          >
                        </div>
                      </td>
                      <td class="bb-roll-pair-col bb-critical-pair-col">
                        <div class="bb-roll-pair-cell">
                          <div class="bb-counter-cell">
                            <CounterEmit
                              :key="`criticals-${statRow.id}`"
                              :pass_ref="
                                buildCounterRef(statRow.id, 'criticals')
                              "
                              :pass_label="'Criticals'"
                              :pass_count="Number(statRow.criticals || 0)"
                              :pass_min="0"
                              :pass_max="99"
                              :pass_digits="2"
                              :disabled="
                                isCounterSaving(statRow.id, 'criticals')
                              "
                              @change="
                                onStatCounterChanged(
                                  statRow,
                                  'criticals',
                                  $event,
                                )
                              "
                            />
                          </div>
                          <div
                            class="form-floating bb-total-field bb-row-metric-field bb-inline-position-field"
                          >
                            <input
                              readonly
                              disabled
                              class="form-control text-center"
                              :value="criticalPositionsById[statRow.id]"
                              :id="`party-rolls-position-${statRow.id}`"
                            />
                            <label
                              class="bb-float-label optional"
                              :for="`party-rolls-position-${statRow.id}`"
                              >Position</label
                            >
                          </div>
                          <div
                            class="form-floating bb-total-field bb-row-metric-field bb-paired-metric-field"
                          >
                            <input
                              readonly
                              disabled
                              class="form-control text-center"
                              :value="
                                formatPercentage(
                                  statRow.criticals || 0,
                                  rowTrackedRolls(statRow),
                                )
                              "
                              :id="`party-rolls-crit-pct-${statRow.id}`"
                            />
                            <label
                              class="bb-float-label optional"
                              :for="`party-rolls-crit-pct-${statRow.id}`"
                              >%age</label
                            >
                          </div>
                        </div>
                      </td>
                      <td class="bb-roll-pair-col bb-fumble-pair-col">
                        <div class="bb-roll-pair-cell">
                          <div class="bb-counter-cell">
                            <CounterEmit
                              :key="`fumbles-${statRow.id}`"
                              :pass_ref="buildCounterRef(statRow.id, 'fumbles')"
                              :pass_label="'Fumbles'"
                              :pass_count="Number(statRow.fumbles || 0)"
                              :pass_min="0"
                              :pass_max="99"
                              :pass_digits="2"
                              :disabled="isCounterSaving(statRow.id, 'fumbles')"
                              @change="
                                onStatCounterChanged(statRow, 'fumbles', $event)
                              "
                            />
                          </div>
                          <div
                            class="form-floating bb-total-field bb-row-metric-field bb-inline-position-field"
                          >
                            <input
                              readonly
                              disabled
                              class="form-control text-center"
                              :value="fumblePositionsById[statRow.id]"
                              :id="`party-rolls-fumble-position-${statRow.id}`"
                            />
                            <label
                              class="bb-float-label optional"
                              :for="`party-rolls-fumble-position-${statRow.id}`"
                              >Position</label
                            >
                          </div>
                          <div
                            class="form-floating bb-total-field bb-row-metric-field bb-paired-metric-field"
                          >
                            <input
                              readonly
                              disabled
                              class="form-control text-center"
                              :value="
                                formatPercentage(
                                  statRow.fumbles || 0,
                                  rowTrackedRolls(statRow),
                                )
                              "
                              :id="`party-rolls-fumble-pct-${statRow.id}`"
                            />
                            <label
                              class="bb-float-label optional"
                              :for="`party-rolls-fumble-pct-${statRow.id}`"
                              >%age</label
                            >
                          </div>
                        </div>
                      </td>
                      <td class="bb-metric-col bb-total-rolls-col">
                        <div
                          class="form-floating bb-total-field bb-row-metric-field"
                        >
                          <input
                            readonly
                            disabled
                            class="form-control text-center"
                            :value="rowTrackedRolls(statRow)"
                            :id="`party-rolls-total-${statRow.id}`"
                          />
                          <label
                            class="bb-float-label optional"
                            :for="`party-rolls-total-${statRow.id}`"
                            >Total Rolls</label
                          >
                        </div>
                      </td>
                      <td class="bb-metric-col">
                        <div
                          v-if="statRow.role !== 'DM'"
                          class="bb-counter-cell"
                        >
                          <CounterEmit
                            :key="`artefact-rolls-${statRow.id}`"
                            :pass_ref="
                              buildCounterRef(statRow.id, 'artefact_rolls')
                            "
                            :pass_label="'Rolls'"
                            :pass_count="Number(statRow.artefact_rolls || 0)"
                            :pass_min="0"
                            :pass_max="99"
                            :pass_digits="2"
                            :disabled="
                              isCounterSaving(statRow.id, 'artefact_rolls')
                            "
                            @change="
                              onStatCounterChanged(
                                statRow,
                                'artefact_rolls',
                                $event,
                              )
                            "
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { axios } from '@/common/api.service.js'
import { mapGetters } from 'vuex'
import CounterEmit from '../components/CounterEmit.vue'
import showNotification from '../helpers/showNotification'
import resolvePartyViewerArenaContext from '../helpers/resolvePartyViewerArenaContext'

export default {
  name: 'PartyRollsView',
  props: {
    fallbackArenaName: {
      type: String,
      default: '',
    },
  },
  components: {
    CounterEmit,
  },
  data() {
    return {
      isLoading: false,
      isSavingUpdates: false,
      savingFieldKey: '',
      errorMessages: [],
      resolvedArenaName: '',
      resolvedArenaSlug: '',
      statsRows: [],
      originalValuesById: {},
      pendingChangesById: {},
    }
  },
  computed: {
    ...mapGetters('auth', {
      getHeaders: 'getHeaders',
    }),
    ...mapGetters('combat', {
      campaign: 'getCampaign',
    }),
    arenaContext() {
      return resolvePartyViewerArenaContext({
        route: this.$route,
        fallbackArenaName: this.fallbackArenaName,
        combatArena: this.campaign,
      })
    },
    arenaSlug() {
      return this.resolvedArenaSlug || this.arenaContext.slug || ''
    },
    arenaName() {
      return (
        this.resolvedArenaName ||
        this.fallbackArenaName ||
        this.campaign?.name ||
        'Current Campaign'
      )
    },
    emptyMessage() {
      if (!this.arenaSlug) {
        return 'No active combat campaign is loaded.'
      }

      return 'No party roll statistics found for this campaign.'
    },
    totalCriticals() {
      return this.statsRows.reduce(
        (total, statRow) => total + Number(statRow?.criticals || 0),
        0,
      )
    },
    totalFumbles() {
      return this.statsRows.reduce(
        (total, statRow) => total + Number(statRow?.fumbles || 0),
        0,
      )
    },
    totalTrackedRolls() {
      return this.totalCriticals + this.totalFumbles
    },
    displayedStatsRows() {
      return this.sortedStatsRows
    },
    overallPositionsById() {
      const positionsById = {}
      let lastRankKey = null
      let currentRank = 0

      this.sortedStatsRows.forEach((statRow) => {
        const rankKey = [
          this.buildRateKey(statRow?.criticals, this.rowTrackedRolls(statRow)),
          this.buildRateKey(statRow?.fumbles, this.rowTrackedRolls(statRow)),
          this.rowNetRolls(statRow),
        ].join(':')

        if (rankKey !== lastRankKey) {
          currentRank += 1
          lastRankKey = rankKey
        }

        positionsById[statRow.id] = currentRank
      })

      return positionsById
    },
    fumblePositionsById() {
      const positionsById = this.statsRows.reduce((accumulator, statRow) => {
        if (statRow?.id != null) {
          accumulator[statRow.id] = 0
        }

        return accumulator
      }, {})

      const rankedRows = this.statsRows
        .map((statRow) => ({
          id: statRow?.id,
          fumbles: Number(statRow?.fumbles || 0),
        }))
        .filter((statRow) => statRow.id && statRow.fumbles > 0)
        .sort((left, right) => right.fumbles - left.fumbles)

      let lastFumbles = null
      let currentRank = 0

      rankedRows.forEach((statRow, index) => {
        if (statRow.fumbles !== lastFumbles) {
          currentRank = index + 1
          lastFumbles = statRow.fumbles
        }

        positionsById[statRow.id] = currentRank
      })

      return positionsById
    },
    criticalPositionsById() {
      const positionsById = this.statsRows.reduce((accumulator, statRow) => {
        if (statRow?.id != null) {
          accumulator[statRow.id] = 0
        }

        return accumulator
      }, {})

      const rankedRows = this.statsRows
        .map((statRow) => ({
          id: statRow?.id,
          criticals: Number(statRow?.criticals || 0),
        }))
        .filter((statRow) => statRow.id && statRow.criticals > 0)
        .sort((left, right) => right.criticals - left.criticals)

      let lastCriticals = null
      let currentRank = 0

      rankedRows.forEach((statRow, index) => {
        if (statRow.criticals !== lastCriticals) {
          currentRank = index + 1
          lastCriticals = statRow.criticals
        }

        positionsById[statRow.id] = currentRank
      })

      return positionsById
    },
    sortedStatsRows() {
      return [...this.statsRows].sort((left, right) =>
        this.compareStatsRows(left, right),
      )
    },
  },
  mounted() {
    this.fetchUserStats()
  },
  methods: {
    compareStatsRows(left, right) {
      const leftTrackedRolls = this.rowTrackedRolls(left)
      const rightTrackedRolls = this.rowTrackedRolls(right)
      const leftCriticals = Number(left?.criticals || 0)
      const rightCriticals = Number(right?.criticals || 0)
      const criticalComparison = this.compareRateDesc(
        leftCriticals,
        leftTrackedRolls,
        rightCriticals,
        rightTrackedRolls,
      )

      if (criticalComparison !== 0) {
        return criticalComparison
      }

      const leftFumbles = Number(left?.fumbles || 0)
      const rightFumbles = Number(right?.fumbles || 0)
      const fumbleComparison = this.compareRateAsc(
        leftFumbles,
        leftTrackedRolls,
        rightFumbles,
        rightTrackedRolls,
      )

      if (fumbleComparison !== 0) {
        return fumbleComparison
      }

      const leftPosition = this.rowNetRolls(left)
      const rightPosition = this.rowNetRolls(right)

      if (leftPosition !== rightPosition) {
        return rightPosition - leftPosition
      }

      return String(left?.first_name || left?.user_name || '').localeCompare(
        String(right?.first_name || right?.user_name || ''),
      )
    },
    normalizeRate(value, total) {
      const numericTotal = Number(total || 0)
      if (numericTotal <= 0) {
        return {
          value: 0,
          total: 1,
        }
      }

      return {
        value: Number(value || 0),
        total: numericTotal,
      }
    },
    greatestCommonDivisor(left, right) {
      let a = Math.abs(Number(left || 0))
      let b = Math.abs(Number(right || 0))

      while (b !== 0) {
        const remainder = a % b
        a = b
        b = remainder
      }

      return a || 1
    },
    buildRateKey(value, total) {
      const normalized = this.normalizeRate(value, total)
      const divisor = this.greatestCommonDivisor(
        normalized.value,
        normalized.total,
      )

      return `${normalized.value / divisor}/${normalized.total / divisor}`
    },
    compareRateDesc(leftValue, leftTotal, rightValue, rightTotal) {
      const normalizedLeft = this.normalizeRate(leftValue, leftTotal)
      const normalizedRight = this.normalizeRate(rightValue, rightTotal)
      const leftCross = normalizedLeft.value * normalizedRight.total
      const rightCross = normalizedRight.value * normalizedLeft.total

      return rightCross - leftCross
    },
    compareRateAsc(leftValue, leftTotal, rightValue, rightTotal) {
      const normalizedLeft = this.normalizeRate(leftValue, leftTotal)
      const normalizedRight = this.normalizeRate(rightValue, rightTotal)
      const leftCross = normalizedLeft.value * normalizedRight.total
      const rightCross = normalizedRight.value * normalizedLeft.total

      return leftCross - rightCross
    },
    formatPercentage(value, total) {
      const numericTotal = Number(total || 0)
      const numericValue = Number(value || 0)

      if (numericTotal <= 0) {
        return '0%'
      }

      return `${Math.round((numericValue / numericTotal) * 100)}%`
    },
    buildCounterRef(statId, fieldName) {
      return `party-rolls-${fieldName}-${statId}`
    },
    buildSavingFieldKey(statId, fieldName) {
      return `${statId}:${fieldName}`
    },
    isCounterSaving(statId, fieldName) {
      return (
        this.isSavingUpdates ||
        this.savingFieldKey === this.buildSavingFieldKey(statId, fieldName)
      )
    },
    trackPendingChange(statId, fieldName, nextValue) {
      this.pendingChangesById = {
        ...this.pendingChangesById,
        [statId]: {
          ...(this.pendingChangesById[statId] || {}),
          [fieldName]: nextValue,
        },
      }
    },
    clearPendingChange(statId, fieldName) {
      const existingChanges = this.pendingChangesById[statId]
      if (!existingChanges || !(fieldName in existingChanges)) {
        return
      }

      const nextChanges = { ...existingChanges }
      delete nextChanges[fieldName]

      const nextPendingChanges = { ...this.pendingChangesById }
      if (Object.keys(nextChanges).length === 0) {
        delete nextPendingChanges[statId]
      } else {
        nextPendingChanges[statId] = nextChanges
      }

      this.pendingChangesById = nextPendingChanges
    },
    hasPendingChanges() {
      return Object.keys(this.pendingChangesById).length > 0
    },
    updateStatRow(statId, updates) {
      this.statsRows = this.statsRows.map((statRow) => {
        if (statRow?.id !== statId) {
          return statRow
        }

        return {
          ...statRow,
          ...updates,
        }
      })
    },
    buildOriginalValuesById(rows) {
      return (rows || []).reduce((result, statRow) => {
        const statId = Number(statRow?.id)
        if (!statId) {
          return result
        }

        result[statId] = {
          criticals: Number(statRow?.criticals || 0),
          fumbles: Number(statRow?.fumbles || 0),
          artefact_rolls: Number(statRow?.artefact_rolls || 0),
          updated_at: statRow?.updated_at || '',
        }

        return result
      }, {})
    },
    rowTrackedRolls(statRow) {
      return Number(statRow?.criticals || 0) + Number(statRow?.fumbles || 0)
    },
    rowNetRolls(statRow) {
      return Number(statRow?.criticals || 0) - Number(statRow?.fumbles || 0)
    },
    normalizeResults(payload) {
      if (Array.isArray(payload)) {
        return payload
      }

      if (Array.isArray(payload?.results)) {
        return payload.results
      }

      return []
    },
    async resolveArenaSlug() {
      this.resolvedArenaName = this.arenaContext.name

      if (!this.arenaContext.slug) {
        console.log('PartyRollsView: missing arena slug for party viewer', {
          routeName: this.arenaContext.routeName,
          isCharacterPartyRoute: this.arenaContext.isCharacterPartyRoute,
          querySlug: this.arenaContext.querySlug,
          paramSlug: this.arenaContext.paramSlug,
          fallbackArenaName: this.fallbackArenaName,
          combatArenaSlug: this.campaign?.slug || '',
        })
        return ''
      }

      this.resolvedArenaSlug = this.arenaContext.slug
      return this.arenaContext.slug
    },
    async onStatCounterChanged(statRow, fieldName, eventPayload) {
      const nextValue = Number(eventPayload?.count)
      const statId = statRow?.id

      if (!statId || Number.isNaN(nextValue) || !this.arenaSlug) {
        return
      }

      const currentValue = Number(statRow?.[fieldName] || 0)
      if (currentValue === nextValue) {
        return
      }

      const originalValue = Number(
        this.originalValuesById?.[statId]?.[fieldName] || 0,
      )

      this.errorMessages = []
      this.updateStatRow(statId, { [fieldName]: nextValue })

      if (nextValue === originalValue) {
        this.clearPendingChange(statId, fieldName)
      } else {
        this.trackPendingChange(statId, fieldName, nextValue)
      }
    },
    async savePendingChanges() {
      const pendingEntries = Object.entries(this.pendingChangesById)
      if (pendingEntries.length === 0) {
        this.pendingChangesById = {}
        return true
      }

      const arenaSlug = await this.resolveArenaSlug()
      if (!arenaSlug) {
        this.errorMessages = [
          window.bbUiErrorMsg('No active combat campaign is loaded.'),
        ]
        return false
      }

      this.isSavingUpdates = true
      this.errorMessages = []

      try {
        for (const [statId, updates] of pendingEntries) {
          const response = await axios.patch(
            `api/v1/campaigns/${arenaSlug}/user-stats/${statId}/`,
            {
              ...updates,
              updated_at: this.originalValuesById?.[statId]?.updated_at || '',
            },
          )

          if (response?.data && typeof response.data === 'object') {
            this.updateStatRow(Number(statId), response.data)
          }
        }

        this.originalValuesById = this.buildOriginalValuesById(this.statsRows)
        this.pendingChangesById = {}
        showNotification('Party rolls updated.')
        return true
      } catch (error) {
        const message =
          error?.response?.data?.detail ||
          'Unable to update party roll statistics.'
        this.errorMessages = [window.bbUiErrorMsg(message)]
        return false
      } finally {
        this.isSavingUpdates = false
      }
    },
    async fetchUserStats() {
      this.isLoading = true
      this.errorMessages = []

      const arenaSlug = await this.resolveArenaSlug()

      if (!arenaSlug) {
        this.statsRows = []
        this.errorMessages = [
          window.bbUiErrorMsg('No active combat campaign is loaded.'),
        ]
        this.isLoading = false
        return
      }

      try {
        const response = await axios.get(
          `api/v1/campaigns/${arenaSlug}/user-stats/`,
        )

        this.statsRows = this.normalizeResults(response.data).sort(
          this.compareStatsRows,
        )
        this.originalValuesById = this.buildOriginalValuesById(this.statsRows)
        this.pendingChangesById = {}
      } catch (error) {
        this.statsRows = []
        this.originalValuesById = {}
        const message =
          error?.response?.data?.detail ||
          'Unable to load party roll statistics.'
        this.errorMessages = [window.bbUiErrorMsg(message)]
      } finally {
        this.isLoading = false
      }
    },
    handleCancel() {
      this.pendingChangesById = {}
      this.errorMessages = []
      this.closeDialog()
    },
    async handleClose() {
      if (this.hasPendingChanges()) {
        const isSaved = await this.savePendingChanges()
        if (!isSaved) {
          return
        }
      }

      this.closeDialog()
    },
    closeDialog() {
      if (this.$route.name) {
        const query = { ...this.$route.query }
        delete query.popup

        if (
          this.$route.name === 'myCharactersView' ||
          this.$route.name === 'combatCharacters'
        ) {
          delete query.slug
        }

        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query,
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
  },
}
</script>

<style scoped>
.bb-popup-outer {
  padding-left: 0 !important;
  padding-right: 1.5rem !important;
  box-sizing: border-box;
}

.bb-popup-table-card {
  /* width: calc(100vw - 1.5rem); */
  width: 95%;
  /* max-width: calc(100vw - 1.5rem); */
  height: min(88vh, 68rem);
  max-height: min(88vh, 68rem);
  position: relative;
  overflow: hidden;
  margin-left: auto;
  margin-right: 0;
}

.bb-popup-table-body {
  padding: 2rem 2rem 1rem !important;
}

.bb-popup-table-loader {
  margin-bottom: 1rem;
}

.bb-rolls-header {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 5rem;
  flex-wrap: wrap;
}

.bb-popup-table-controls {
  width: 100%;
  margin-top: 1.5rem;
}

.bb-roll-summary-row {
  margin-right: 0;
}

.bb-overall-summary-field {
  max-width: 10.5rem;
}

.bb-overall-summary-field :deep(.form-control) {
  min-width: 0;
}

.bb-popup-table-actions {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  padding-bottom: 0.65rem;
}

.bb-popup-table-actions .btn {
  width: auto;
  min-width: 8rem;
  margin-bottom: 0;
}

.bb-counter-cell {
  min-width: 15.5rem;
  width: 15.5rem;
}

.bb-roll-pair-cell {
  display: flex;
  align-items: stretch;
  gap: 0.85rem;
  min-width: 0;
}

.bb-total-field {
  min-width: 9.8rem;
}

.bb-row-metric-field {
  min-width: 6rem;
}

.bb-paired-metric-field {
  min-width: 7.25rem;
  width: 7.25rem;
  flex: 0 0 7.25rem;
}

.bb-inline-position-field {
  min-width: 8rem;
  width: 8rem;
  flex: 0 0 8rem;
}

.bb-counter-cell :deep(.d-flex) {
  gap: 0.5rem;
  width: 100%;
  flex-wrap: nowrap;
}

.bb-counter-cell :deep(.btn.btn-lg) {
  min-width: 3.65rem;
  padding-left: 0.9rem;
  padding-right: 0.9rem;
  flex: 0 0 3.65rem;
}

.bb-counter-cell :deep(.col.form-group) {
  min-width: 0;
  width: auto;
  flex: 1 1 auto;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.bb-counter-cell :deep(.form-control) {
  min-width: 0;
  width: 100%;
}

:deep(.table-responsive) {
  overflow-x: auto;
}

:deep(.table) {
  min-width: 108rem;
  color: inherit;
}

:deep(.table > :not(caption) > * > *) {
  background-color: transparent;
  color: inherit;
  border-bottom: 0;
  box-shadow: none;
}

:deep(.table th.bb-metric-col),
:deep(.table td.bb-metric-col) {
  width: 8rem;
  min-width: 8rem;
}

:deep(.table th.bb-overall-position-col),
:deep(.table td.bb-overall-position-col) {
  width: 10rem;
  min-width: 10rem;
}

:deep(.table th.bb-net-rolls-col),
:deep(.table td.bb-net-rolls-col) {
  width: 9.25rem;
  min-width: 9.25rem;
}

:deep(.table th.bb-roll-pair-col),
:deep(.table td.bb-roll-pair-col) {
  width: 33rem;
  min-width: 33rem;
}

:deep(.table th.bb-critical-pair-col),
:deep(.table td.bb-critical-pair-col) {
  padding-left: 1rem;
  padding-right: 0.45rem;
}

:deep(.table th.bb-fumble-pair-col),
:deep(.table td.bb-fumble-pair-col) {
  width: 32.4rem;
  min-width: 32.4rem;
  padding-left: 0;
  padding-right: 0.25rem;
}

:deep(.table th.bb-total-rolls-col),
:deep(.table td.bb-total-rolls-col) {
  width: 7.25rem;
  min-width: 7.25rem;
  padding-left: 0.25rem;
}

:deep(.table td.bb-total-rolls-col) .bb-total-field {
  min-width: 7rem;
  width: 7rem;
  margin-left: 0;
}

:deep(.table th.bb-user-col),
:deep(.table td.bb-user-col) {
  width: 14rem;
  min-width: 14rem;
}

.bb-user-field {
  min-width: 13rem;
}

.bb-user-field :deep(.form-control) {
  text-align: left !important;
}

:deep(.table th.bb-role-col),
:deep(.table td.bb-role-col) {
  width: 10rem;
  min-width: 10rem;
}

.bb-role-field {
  min-width: 9rem;
}

.bb-overall-position-field {
  min-width: 9rem;
}

.bb-net-rolls-field {
  min-width: 9rem;
}

:deep(.table thead th) {
  font-size: 1.2rem;
}

:deep(.table td.bb-roll-pair-col),
:deep(.table td.bb-metric-col),
:deep(.table td.bb-overall-position-col),
:deep(.table td.bb-net-rolls-col) {
  overflow: hidden;
}

.bb-counter-cell {
  overflow: hidden;
}

@media (max-width: 768px) {
  .bb-popup-table-card {
    width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
    height: calc(100vh - 1rem);
  }

  .bb-rolls-header {
    gap: 0.75rem;
  }

  .bb-popup-table-controls {
    width: 100%;
  }

  .bb-popup-table-actions {
    justify-content: flex-start;
  }

  .bb-popup-table-body {
    padding: 1.25rem 1.25rem 1rem !important;
  }
}
</style>
